import React from 'react';
import Select from 'react-select';
import { Tab, Tabs } from 'react-bootstrap';
import { UseFormGetValues, UseFormRegister } from 'react-hook-form';
import { ReadLotacaoDto } from '@/api.types';
import { IOpcao, SelectOption } from '@/shared/types/selectOption';
import { TipoLotacaoList } from '@/shared/constants/enumlist';
import { TipoLotacao } from './enums/tipo-lotacao.enum';
import { TipoPessoaLotada } from './enums/tipo-pessoa-lotada.enum';

type RefSelect = React.Ref<any>;

const selectMenuAcima = {
  menuPortalTarget: typeof document === 'undefined' ? undefined : document.body,
  menuPosition: 'fixed' as const,
  styles: {
    menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }),
  },
};

export interface EditLotacaoCardProps {
  form: {
    register: UseFormRegister<ReadLotacaoDto>;
    getValues: UseFormGetValues<ReadLotacaoDto>;
  };

  tipoPessoaLotada: TipoPessoaLotada;
  ocultarSelecaoPessoa?: boolean;
  /** `cards` = página de vínculos; `abas` = modal na ficha da pessoa. */
  layout?: 'cards' | 'abas';

  pessoa: {
    colaboradores: IOpcao[];
    defensores: IOpcao[];
    estagiarioid: number;
    estagiarios: IOpcao[];
    estagioid: number;
    estagios: IOpcao[];
    refSelectColaborador: RefSelect;
    refSelectDefensor: RefSelect;
    refSelectEstagiario: RefSelect;
    refSelectEstagio: RefSelect;
    handleSelectChange: (
      selectedOption: IOpcao | null,
      options: IOpcao[],
      field: 'defensor' | 'colaborador' | 'estagiario' | 'estagio',
    ) => Promise<void>;
  };

  dados: {
    pessoas: SelectOption[];
    refSelectResponsavel: RefSelect;
    handleSelectChange: (
      selectedOption: SelectOption | null,
      options: SelectOption[],
      field: 'responsavel',
    ) => void;
  };

  local: {
    defensorias: IOpcao[];
    nucleos: IOpcao[];
    predios: IOpcao[];
    setoresDisponiveis: IOpcao[];
    refSelectDefensoria: RefSelect;
    refSelectNucleo: RefSelect;
    refSelectPredio: RefSelect;
    refSelectSetor: RefSelect;
    handleSelectChange: (
      selectedOption: IOpcao | null,
      options: IOpcao[],
      field: 'defensoria' | 'nucleo' | 'predio' | 'setor',
    ) => void;
  };
}

export function EditLotacaoCard({
  form,
  tipoPessoaLotada,
  ocultarSelecaoPessoa = false,
  layout = 'cards',
  pessoa,
  dados,
  local,
}: EditLotacaoCardProps) {
  const { register, getValues } = form;
  const abas = layout === 'abas';

  const secaoDados = (
    <div className="row g-2">
      {!ocultarSelecaoPessoa && (
        <>
          <div
            className="col-12"
            style={{ display: tipoPessoaLotada === TipoPessoaLotada.Defensor ? 'block' : 'none' }}
          >
            <label htmlFor="defensor" className="form-label">
              Defensor:
            </label>
            <Select
              {...selectMenuAcima}
              id="defensor"
              ref={pessoa.refSelectDefensor}
              isClearable
              value={pessoa.defensores.find((a) => (a ? a.value === getValues()?.defensor?.id : null))}
              options={pessoa.defensores}
              placeholder=""
              onChange={(e) => void pessoa.handleSelectChange(e, pessoa.defensores, 'defensor')}
            />
          </div>

          <div
            className="col-12"
            style={{ display: tipoPessoaLotada === TipoPessoaLotada.Colaborador ? 'block' : 'none' }}
          >
            <label htmlFor="colaborador" className="form-label">
              Colaborador:
            </label>
            <Select
              {...selectMenuAcima}
              id="colaborador"
              ref={pessoa.refSelectColaborador}
              isClearable
              value={pessoa.colaboradores.find(
                (a) => String(a.value) === String(getValues()?.colaborador?.id),
              )}
              options={pessoa.colaboradores}
              placeholder=""
              onChange={(e) => {
                void pessoa.handleSelectChange(e, pessoa.colaboradores, 'colaborador');
              }}
            />
          </div>

          <div
            className="col-12"
            style={{ display: tipoPessoaLotada === TipoPessoaLotada.Estagiario ? 'block' : 'none' }}
          >
            <label htmlFor="estagiario" className="form-label">
              Estagiário:{' '}
            </label>
            <Select
              {...selectMenuAcima}
              id="estagiario"
              ref={pessoa.refSelectEstagiario}
              isClearable
              value={pessoa.estagiarios.find(
                (a) =>
                  a.value === getValues()?.estagio?.estagiario?.id || a.value === pessoa.estagiarioid,
              )}
              options={pessoa.estagiarios}
              placeholder=""
              onChange={(e) => {
                void pessoa.handleSelectChange(e, pessoa.estagiarios, 'estagiario');
              }}
            />
          </div>
        </>
      )}

      <div className="col-md-6" title="Tipo de vínculo">
        <label htmlFor="tipo" className="form-label">
          Tipo:
        </label>
        <select id="tipo" className="form-select" {...register('tipo')}>
          {TipoLotacaoList.filter(
            (a) =>
              (tipoPessoaLotada !== TipoPessoaLotada.Estagiario &&
                tipoPessoaLotada !== TipoPessoaLotada.Colaborador) ||
              (tipoPessoaLotada === TipoPessoaLotada.Estagiario &&
                a.value === TipoLotacao.Designacao) ||
              (tipoPessoaLotada === TipoPessoaLotada.Colaborador &&
                a.value === TipoLotacao.Designacao),
          ).map((item) => (
            <option key={item.value} value={item.value}>
              {item.name}
            </option>
          ))}
        </select>
      </div>

      <div className="col-md-6">
        <label className="form-label" htmlFor="portaria">
          Portaria:
        </label>
        <input id="portaria" type="text" className="form-control" {...register('portaria')} />
      </div>

      <div className="col-12 d-flex flex-wrap gap-3 align-items-center mt-2 mb-2">
        <div className="form-check mb-0" title="Desmarque se for apenas rascunho.">
          <input id="ativo" type="checkbox" className="form-check-input" {...register('ativo')} />
          <label className="form-check-label" htmlFor="ativo">
            Ativo.
          </label>
        </div>

        <div
          className="form-check mb-0"
          title="Permite ao estagiário marcar qualquer dia em sua folha e não valida se todos os dias foram preenchidos."
        >
          <input
            id="diaslivres"
            type="checkbox"
            className="form-check-input"
            {...register('diaslivres')}
          />
          <label className="form-check-label" htmlFor="diaslivres">
            Dias Livres.
          </label>
        </div>

        <div className="form-check mb-0" title="Se deve pontuar">
          <input
            id="pontuar"
            type="checkbox"
            className="form-check-input"
            disabled={tipoPessoaLotada !== TipoPessoaLotada.Defensor}
            {...register('pontuar')}
          />
          <label className="form-check-label" htmlFor="pontuar">
            Pontuar.
          </label>
        </div>

        <div
          className="form-check mb-0"
          title="Diretor no núcleo (defensor) ou no setor (colaborador)."
        >
          <input
            id="diretor"
            type="checkbox"
            className="form-check-input"
            disabled={
              tipoPessoaLotada !== TipoPessoaLotada.Defensor &&
              tipoPessoaLotada !== TipoPessoaLotada.Colaborador
            }
            {...register('diretor')}
          />
          <label className="form-check-label" htmlFor="diretor">
            Diretor(a).
          </label>
        </div>
      </div>

      <div className="col-12">
        <hr className="my-2" />
      </div>

      <div className="col-12 mb-2">
        <label htmlFor="estagiovinculo" className="form-label">
          Estágio:{' '}
        </label>
        <Select
          {...selectMenuAcima}
          id="estagiovinculo"
          ref={pessoa.refSelectEstagio}
          isDisabled={tipoPessoaLotada !== TipoPessoaLotada.Estagiario}
          isClearable
          defaultValue={
            pessoa.estagios.find((a) => a.value === getValues()?.estagio?.id) ||
            pessoa.estagios.find((a) => a.value === pessoa.estagioid)
          }
          options={pessoa.estagios}
          placeholder=""
          onChange={(e) => {
            void pessoa.handleSelectChange(e, pessoa.estagios, 'estagio');
          }}
        />
      </div>
      <div className="col-12" title="Responsável pelo estagiário.">
        <label htmlFor="responsavel" className="form-label">
          Responsável:
        </label>
        <Select
          {...selectMenuAcima}
          isDisabled={tipoPessoaLotada !== TipoPessoaLotada.Estagiario}
          id="responsavel"
          ref={dados.refSelectResponsavel}
          isClearable
          defaultValue={dados.pessoas.find((a) => a.value === getValues()?.responsavel?.id)}
          options={dados.pessoas}
          placeholder=""
          onChange={(e) => {
            dados.handleSelectChange(e, dados.pessoas, 'responsavel');
          }}
        />
      </div>
    </div>
  );

  const secaoLocal = (
    <div className="row g-2">
      <div className="col-12">
        <label className="form-label" htmlFor="defensoria">
          Defensoria
        </label>
        <Select
          {...selectMenuAcima}
          id="defensoria"
          ref={local.refSelectDefensoria}
          isClearable
          defaultValue={local.defensorias.find((a) => a.value === getValues()?.defensoria?.id)}
          options={local.defensorias}
          placeholder=""
          onChange={(e) => {
            local.handleSelectChange(e, local.defensorias, 'defensoria');
          }}
        />
      </div>
      <div className="col-12">
        <label className="form-label" htmlFor="nucleo">
          Núcleo
        </label>
        <Select
          {...selectMenuAcima}
          id="nucleo"
          ref={local.refSelectNucleo}
          isClearable
          defaultValue={local.nucleos.find((a) => a.value === getValues()?.nucleo?.id)}
          options={local.nucleos}
          placeholder=""
          onChange={(e) => local.handleSelectChange(e, local.nucleos, 'nucleo')}
        />
      </div>

      <div className="col-12">
        <hr className="my-2" />
      </div>

      <div className="col-md-6">
        <label htmlFor="predio" className="form-label">
          Prédio:
        </label>
        <Select
          {...selectMenuAcima}
          id="predio"
          ref={local.refSelectPredio}
          isClearable
          defaultValue={local.predios.find((a) => a.value === getValues()?.setor?.predio?.id)}
          options={local.predios}
          placeholder=""
          onChange={(e) => local.handleSelectChange(e, local.predios, 'predio')}
        />
      </div>

      <div className="col-md-6">
        <label htmlFor="setor" className="form-label">
          Setor:
        </label>
        <Select
          {...selectMenuAcima}
          id="setor"
          ref={local.refSelectSetor}
          isClearable
          value={local.setoresDisponiveis.find((a) => a.value === getValues()?.setor?.id)}
          options={local.setoresDisponiveis}
          placeholder=""
          onChange={(e) => local.handleSelectChange(e, local.setoresDisponiveis, 'setor')}
        />
      </div>
    </div>
  );

  const secaoVigencia = (
    <div className="row g-2">
      <div className="col-md-6" title="Início do período de vínculo.">
        <label htmlFor="data_inicial" className="form-label">
          Início:
        </label>
        <input id="data_inicial" type="date" className="form-control" {...register('data_inicial')} />
      </div>
      <div className="col-md-6" title="Término do período de vínculo.">
        <label htmlFor="data_final" className="form-label">
          Término:
        </label>
        <input id="data_final" type="date" className="form-control" {...register('data_final')} />
      </div>
      <div className="col-6" title="Início do expediente (opcional).">
        <label htmlFor="entrada" className="form-label">
          Entrada:
        </label>
        <input id="entrada" type="time" className="form-control" {...register('entrada')} />
      </div>
      <div className="col-6" title="Término do expediente (opcional).">
        <label htmlFor="Saida" className="form-label">
          Saída:
        </label>
        <input id="Saida" type="time" className="form-control" {...register('saida')} />
      </div>
    </div>
  );

  const secaoAnotacoes = (
    <div className="row g-2">
      <div className="col-12">
        <label className="form-label" htmlFor="anotacao2">
          Observação:
        </label>
        <input id="anotacao2" type="text" className="form-control" {...register('anotacao2')} />
      </div>
      <div className="col-12">
        <label className="form-label mt-1" htmlFor="anotacao1">
          Anotação:
        </label>
        <textarea id="anotacao1" className="form-control" rows={3} {...register('anotacao1')} />
        <span className="blockquote-footer text-start">Visível nos relatórios.</span>
      </div>
    </div>
  );

  if (abas) {
    return (
      <Tabs
        defaultActiveKey="dados"
        id="lotacao-modal-abas"
        className="mb-3 lotacao-form-abas"
        justify
      >
        <Tab eventKey="dados" title="Dados do vínculo">
          <div className="pt-2 px-1">{secaoDados}</div>
        </Tab>
        <Tab eventKey="local" title="Local da atuação">
          <div className="pt-2 px-1">{secaoLocal}</div>
        </Tab>
        <Tab eventKey="vigencia" title="Vigência e observações">
          <div className="pt-2 px-1">
            {secaoVigencia}
            <hr className="my-3" />
            {secaoAnotacoes}
          </div>
        </Tab>
      </Tabs>
    );
  }

  return (
    <div className="row">
      <div className="col-md-5 mb-4">
        <div className="card">
          <div className="card-header text-center">Dados do Vínculo</div>
          <div className="card-body">{secaoDados}</div>
        </div>
      </div>

      <div className="col-md-4">
        <div className="card mb-3">
          <div className="card-header text-center">Local da Atuação</div>
          <div className="card-body">{secaoLocal}</div>
        </div>
      </div>

      <div className="col-md-3">
        <div className="card mb-3">
          <div className="card-header text-center">Vigência e Expediente</div>
          <div className="card-body">{secaoVigencia}</div>
        </div>
        <div className="card">
          <div className="card-header text-center">Anotações e Observações</div>
          <div className="card-body">{secaoAnotacoes}</div>
        </div>
      </div>
    </div>
  );
}
